import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Alert,
  Snackbar,
  TablePagination,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";

import AddRedirectModal from "./components/AddRedirectModal";
import BulkPasteModal from "./components/Bulkpastemodal";
import DeleteConfirmModal from "./components/Deleteconfirmmodal";
import UploadPDFModal from "./components/Uploadcsvmodal";

import {
  getRedirects,
  createRedirect,
  updateRedirect,
  deleteRedirect,
  bulkCreateRedirects,
  bulkUploadRedirectFile,
} from "./components/RedirectionApis";


const toUi = (item) => ({
  id: item._id || item.id,
  from_path: item.source,
  to_url: item.destination,
  status_code: item.type,
  is_active: item.status,
  created_at: item.createdAt ? item.createdAt.slice(0, 10) : "",
});

// ── Map UI shape → API payload ────────────────────────────────────────────────
const toApi = (uiRow) => ({
  source: uiRow.from_path,
  destination: uiRow.to_url,
  type: Number(uiRow.status_code),
  status: uiRow.is_active,
});

export default function RedirectsPage() {
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAll = useCallback(async () => {
    setPageLoading(true);
    const result = await getRedirects({ page: page, limit: rowsPerPage });
    if (result?.error) {
      showToast(result.error, "error");
    } else {
      const mapped = (Array.isArray(result) ? result : []).map(toUi);
      setRows(mapped);
    }
    setPageLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Filtering ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let data = [...rows];
    if (tab === 1) data = data.filter((r) => r.is_active);
    if (tab === 2) data = data.filter((r) => !r.is_active);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.from_path?.toLowerCase().includes(q) ||
          r.to_url?.toLowerCase().includes(q)
      );
    }
    setFiltered(data);
    setPage(0);
  }, [rows, search, tab]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (msg, severity = "success") =>
    setToast({ open: true, msg, severity });

  // ── Toggle active ─────────────────────────────────────────────────────────
  const handleToggleActive = async (row) => {
    setLoading(true);
    const result = await updateRedirect(row.id, { status: !row.is_active });
    if (result?.error) {
      showToast(result.error, "error");
    } else {
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, is_active: !r.is_active } : r
        )
      );
      showToast(`Redirect ${!row.is_active ? "activated" : "deactivated"}`);
    }
    setLoading(false);
  };

  // ── Add / Edit save ───────────────────────────────────────────────────────
  const handleSave = async (data) => {
    setLoading(true);
    const apiPayload = toApi(data);

    if (editTarget) {
      const result = await updateRedirect(editTarget.id, apiPayload);
      if (result?.error) {
        showToast(result.error, "error");
      } else {
        setRows((prev) =>
          prev.map((r) =>
            r.id === editTarget.id ? { ...r, ...toUi({ ...apiPayload, _id: editTarget.id, createdAt: editTarget.created_at }) } : r
          )
        );
        showToast("Redirect updated");
      }
    } else {
      const result = await createRedirect(apiPayload);
      if (result?.error) {
        showToast(result.error, "error");
      } else {
        // Use returned object if available, else build from payload
        const newRow = toUi(
          result?._id || result?.id
            ? result
            : { ...apiPayload, _id: Date.now().toString(), createdAt: new Date().toISOString() }
        );
        setRows((prev) => [newRow, ...prev]);
        showToast("Redirect created");
      }
    }

    setEditTarget(null);
    setAddOpen(false);
    setLoading(false);
  };

  // ── Bulk add ──────────────────────────────────────────────────────────────
  const handleBulkSave = async (newRows) => {
    setLoading(true);
    // newRows come in UI shape from the modal
    const apiPayloads = newRows.map((r) => toApi(r));
    const result = await bulkCreateRedirects(apiPayloads);

    if (result?.error) {
      showToast(result.error, "error");
    } else {
      // Re-fetch so IDs are correct
      await fetchAll();
      showToast(`${newRows.length} redirect(s) added`);
    }

    setPasteOpen(false);
    setUploadOpen(false);
    setLoading(false);
  };


  const handleFileUpload = async (file) => {
    setLoading(true);
    const result = await bulkUploadRedirectFile(file);
    if (result?.error) {
      showToast(result.error, "error");
    } else {
      await fetchAll();
      showToast("File uploaded successfully");
    }
    setUploadOpen(false);
    setLoading(false);
  };


  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setLoading(true);
    const result = await deleteRedirect(deleteTarget.id);
    if (result?.error) {
      showToast(result.error, "error");
    } else {
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      showToast("Redirect deleted", "warning");
    }
    setDeleteTarget(null);
    setLoading(false);
  };

  // ── Export CSV ────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const header = "from_path,to_url,status_code,is_active\n";
    const body = filtered
      .map((r) => `${r.from_path},${r.to_url},${r.status_code},${r.is_active}`)
      .join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "redirects.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalActive = rows.filter((r) => r.is_active).length;
  const totalInactive = rows.length - totalActive;

  const paginatedRows = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: "100%" }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ fontSize: { xs: "1.3rem", md: "1.5rem" } }}
        >
          URL Redirects
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ContentPasteIcon />}
            onClick={() => setPasteOpen(true)}
            sx={outlinedBtnStyle}
          >
            Paste bulk
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setUploadOpen(true)}
            sx={outlinedBtnStyle}
          >
            Upload file
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            sx={outlinedBtnStyle}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditTarget(null);
              setAddOpen(true);
            }}
            sx={primaryBtnStyle}
          >
            Add redirect
          </Button>
        </Stack>
      </Box>

      {/* ── Stat cards ── */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <StatCard label="Total" value={rows.length} color="#1976d2" />
        <StatCard label="Active" value={totalActive} color="#2e7d32" />
        <StatCard label="Inactive" value={totalInactive} color="#9e9e9e" />
      </Stack>

      {/* ── Filters + Table ── */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          mb: 0.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minWidth: 80,
              },
              "& .Mui-selected": { color: "#1976d2 !important" },
              "& .MuiTabs-indicator": { backgroundColor: "#1976d2" },
            }}
          >
            <Tab label={`All (${rows.length})`} />
            <Tab label={`Active (${totalActive})`} />
            <Tab label={`Inactive (${totalInactive})`} />
          </Tabs>

          <Box sx={{ ml: "auto", pb: 1 }}>
            <TextField
              size="small"
              placeholder="Search paths or URLs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 260 },
                "& fieldset": { borderRadius: 2 },
              }}
            />
          </Box>
        </Box>
        <Divider />

        <TableContainer>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell sx={thStyle}>#</TableCell>
                <TableCell sx={thStyle}>From path</TableCell>
                <TableCell sx={thStyle}>Redirect to</TableCell>
                <TableCell sx={thStyle} align="center">
                  Type
                </TableCell>
                <TableCell sx={thStyle} align="center">
                  Status
                </TableCell>
                <TableCell sx={thStyle} align="center">
                  Active
                </TableCell>
                <TableCell sx={thStyle} align="center">
                  Edit
                </TableCell>
                <TableCell sx={thStyle} align="center">
                  Delete
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{ py: 6, color: "#9e9e9e" }}
                  >
                    <LinkIcon
                      sx={{
                        fontSize: 40,
                        mb: 1,
                        opacity: 0.3,
                        display: "block",
                        mx: "auto",
                      }}
                    />
                    No redirects found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row, idx) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      opacity: row.is_active ? 1 : 0.55,
                      "&:last-child td": { border: 0 },
                      transition: "opacity 0.2s",
                    }}
                  >
                    <TableCell sx={{ color: "#9e9e9e", fontSize: 13 }}>
                      {page * rowsPerPage + idx + 1}
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 13,
                          color: "#1976d2",
                          fontWeight: 500,
                        }}
                      >
                        {row.from_path}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#2e7d32",
                          fontFamily: "monospace",
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.to_url}
                      >
                        {row.to_url}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.status_code ? `${row.status_code}` : "—"}
                        size="small"
                        sx={{
                          backgroundColor:
                            row.status_code === 301
                              ? "#e3f2fd"
                              : row.status_code === 302
                                ? "#fff3e0"
                                : "#f5f5f5",
                          color:
                            row.status_code === 301
                              ? "#1565c0"
                              : row.status_code === 302
                                ? "#e65100"
                                : "#9e9e9e",
                          fontWeight: 600,
                          fontSize: 12,
                          height: 24,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.is_active ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          backgroundColor: row.is_active ? "#e8f5e9" : "#f5f5f5",
                          color: row.is_active ? "#2e7d32" : "#757575",
                          fontWeight: 500,
                          fontSize: 12,
                          height: 24,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={row.is_active}
                        onChange={() => handleToggleActive(row)}
                        size="small"
                        disabled={loading}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#1976d2",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            { backgroundColor: "#1976d2" },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditTarget(row);
                          setAddOpen(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(row)}
                        sx={{ color: "#d32f2f" }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setPage(0);
          }}
          sx={{
            "& .MuiTablePagination-select": {
              paddingRight: "28px !important",
              paddingLeft: "8px",
              backgroundColor: "#f0f7ff",
              borderRadius: "6px",
              border: "1px solid #bbdefb",
              color: "#1565c0",
              fontWeight: 500,
              fontSize: 13,
              minWidth: 40,
            },
            "& .MuiTablePagination-selectIcon": {
              color: "#1565c0",
              right: 4,
            },
          }}
        />
      </Paper>

      {/* ── Modals ── */}
      <AddRedirectModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setEditTarget(null);
        }}
        onSave={handleSave}
        editData={editTarget}
        loading={loading}
      />
      <BulkPasteModal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onSave={handleBulkSave}
        loading={loading}
      />
      <UploadPDFModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSave={handleFileUpload}
        loading={loading}
      />
      <DeleteConfirmModal
        open={!!deleteTarget}
        row={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={loading}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const thStyle = {
  fontWeight: 600,
  fontSize: 13,
  color: "#424242",
  borderBottom: "1px solid #e0e0e0",
  py: 1.5,
};

const primaryBtnStyle = {
  backgroundColor: "#1976d2",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 1.5,
  px: 2,
  "&:hover": { backgroundColor: "#1565c0" },
};

const outlinedBtnStyle = {
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 1.5,
  borderColor: "#bdbdbd",
  color: "#424242",
  "&:hover": {
    borderColor: "#1976d2",
    color: "#1976d2",
    backgroundColor: "#f0f7ff",
  },
};

function StatCard({ label, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        px: 2.5,
        py: 1.5,
        minWidth: 100,
        borderTop: `3px solid ${color}`,
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#9e9e9e", mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color }}>
        {value}
      </Typography>
    </Paper>
  );
}